/* @jsx h */
import { defineComponent, ref, h, onMounted, onUnmounted, watch } from "vue";
import { LemmonModal } from "@lemmonhq/uikit";
import { useCheckout, useAuth } from "../../providers";
import { api, instrumentation } from "@app/data/index";
import { routingFetch } from "@app/data/helpers";
import { CheckoutData } from "@lemmonhq/types";
import {
  usePaymentResilience,
  TransactionStatus,
} from "../../composables/usePaymentResilience";

interface TerminalPaymentModalProps {
  onClose: () => void;
  onConfirm: (
    amountReceived: number,
    paymentId: number,
    tipAmount?: number,
  ) => void;
  totalAmount: number;
  checkoutId: string;
  paymentMethod: "card" | "twint";
}

export const TerminalPaymentModal = defineComponent<TerminalPaymentModalProps>({
  name: "TerminalPaymentModal",
  props: ["onClose", "onConfirm", "totalAmount", "checkoutId", "paymentMethod"],
  setup(props) {
    const transactionId = ref<number | null>(null);
    const status = ref<TransactionStatus>("initializing");
    const statusMessage = ref("Initializing payment terminal...");
    const aborted = ref(false);
    const checkout = useCheckout();
    const auth = useAuth();
    const processedPayment = ref(false);

    // Separate resilience instances for each operation to avoid shared state
    const resilienceConfig = {
      maxRetries: 3,
      maxConsecutiveFailures: 500,
      transactionTimeout: 10 * 60 * 1000, // 10 minutes for terminal payments
      initialPollingInterval: import.meta.env.VITE_LOCALHOST ? 3000 : 2000,
      maxPollingInterval: 30000,
      retryBaseDelay: 2000,
      maxRetryDelay: 8000,
      pollingBackoffMultiplier: 1.5,
    };

    // Separate instance for transaction initialization
    const transactionResilience = usePaymentResilience(resilienceConfig);

    // Separate instance for polling
    const pollingResilience = usePaymentResilience(resilienceConfig);

    // Separate instance for payment processing
    const processingResilience = usePaymentResilience(resilienceConfig);

    // Strategy 4: Retry Logic for Initial Transaction
    // Strategy 5: AbortController cancels in-flight requests during retries
    const startTransaction = async () => {
      try {
        status.value = "initializing";

        instrumentation.track("checkout_terminal_payment_initiated", {
          checkout_id: props.checkoutId,
          payment_method: props.paymentMethod,
          payment_amount: props.totalAmount,
        });

        // Use retry logic with exponential backoff and request cancellation
        await transactionResilience.executeWithRetry(
          async (signal: AbortSignal) => {
            statusMessage.value = "Connecting to payment terminal...";

            if (!checkout.checkout.value) throw new Error("no checkout");

            const checkoutData: CheckoutData = {
              ...checkout.checkout.value,
              payment: {
                amountRcvd: props.totalAmount,
                paymentType: props.paymentMethod,
              },
            };
            checkout.setCheckout(checkoutData);
            const checkoutId = props.checkoutId;

            // Pass the abort signal to cancel in-flight requests during retries
            const data = await api.createPaymentTx(
              props.totalAmount,
              checkout.checkout.value,
              checkoutId,
              props.paymentMethod,
              signal,
            );

            // Strategy 3: Start transaction timer
            pollingResilience.startTransactionTimer();

            transactionId.value = data.id;
            status.value = "processing";
            statusMessage.value =
              "Processing payment. Please follow instructions on terminal...";

            // Start polling for transaction status
            pollingResilience.startPolling(pollTransactionStatus);
          },
          (retryCount, delay) => {
            // Callback on retry
            statusMessage.value = `Connecting to terminal... (Retry ${retryCount}/${
              transactionResilience.maxRetries
            }) - Retrying in ${delay / 1000}s...`;
          },
        );
      } catch (error: any) {
        console.error("Error initiating Terminal transaction:", error);

        // Strategy 6: Better Error Messaging
        const errorContext = transactionResilience.analyzeError(error, 0);
        status.value = "errored";
        statusMessage.value = errorContext.isNetworkError
          ? "Network error: Failed to connect to terminal. Please check your connection."
          : error.message || "Failed to initiate payment. Please try again.";

        instrumentation.track("checkout_terminal_payment_initiate_failed", {
          checkout_id: props.checkoutId,
          payment_method: props.paymentMethod,
          payment_amount: props.totalAmount,
          error_message: error?.message,
        });
      }
    };

    const pollTransactionStatus = async () => {
      if (!transactionId.value || aborted.value) return;

      // Strategy 3: Transaction Timeout Handling
      if (pollingResilience.hasTransactionTimedOut()) {
        status.value = "errored";
        statusMessage.value =
          "Transaction timeout. Payment terminal took too long.";
        pollingResilience.stopPolling();
        instrumentation.track("checkout_terminal_payment_polling_stalled", {
          checkout_id: props.checkoutId,
          payment_transaction_id: transactionId.value,
          payment_method: props.paymentMethod,
          reason: "timeout",
        });
        return;
      }

      // Strategy 1: Network Status Detection - Skip polling while offline
      if (!pollingResilience.isOnline.value) {
        return; // Will resume when online
      }

      // Strategy 2: Circuit Breaker Pattern
      if (pollingResilience.shouldCircuitBreak()) {
        status.value = "errored";
        statusMessage.value =
          "Too many network errors. Please check your connection and try again.";
        pollingResilience.stopPolling();
        instrumentation.track("checkout_terminal_payment_polling_stalled", {
          checkout_id: props.checkoutId,
          payment_transaction_id: transactionId.value,
          payment_method: props.paymentMethod,
          reason: "circuit_breaker",
          consecutive_failures: pollingResilience.consecutiveFailures.value,
        });
        return;
      }

      // Strategy 5: AbortController for Request Cancellation
      const signal = pollingResilience.createAbortSignal();

      instrumentation.track("checkout_terminal_payment_poll_attempted", {
        checkout_id: props.checkoutId,
        payment_transaction_id: transactionId.value,
        payment_method: props.paymentMethod,
      });

      try {
        const data = await api.checkTerminalPaymentResult(
          transactionId.value,
          signal,
        );
        console.log("pollTransactionStatus data", data);
        const { payment, status: terminalStatus } = data;

        instrumentation.track("checkout_terminal_payment_poll_success", {
          checkout_id: props.checkoutId,
          payment_transaction_id: transactionId.value,
          payment_method: props.paymentMethod,
          payment_terminal_status: terminalStatus?.status,
        });

        // Reset failures and polling interval on successful request
        pollingResilience.resetFailures();
        pollingResilience.resetPollingInterval();

        if (terminalStatus?.status === "SUCCESS") {
          status.value = "success";
          statusMessage.value = "Payment successful!";
          pollingResilience.stopPolling();

          // Idempotency guard: prevent duplicate payment processing
          if (processedPayment.value) return;
          processedPayment.value = true;

          try {
            // Use retry logic for processing payment with resilience
            await processingResilience.executeWithRetry(
              async (signal: AbortSignal) => {
                await api.processPaymentTx(payment.id, signal);
              },
              (retryCount, delay) => {
                statusMessage.value = `Processing payment... (Retry ${retryCount}/${processingResilience.maxRetries})`;
              },
            );
          } catch (e) {
            console.log("pollTransactionStatus", e);
            instrumentation.track("checkout_terminal_payment_process_failed", {
              checkout_id: props.checkoutId,
              payment_id: payment.id,
              payment_method: props.paymentMethod,
            });
            throw new Error("Failed to process Terminal transaction");
          }
          instrumentation.track("checkout_terminal_payment_completed", {
            checkout_id: props.checkoutId,
            payment_id: payment.id,
            payment_method: props.paymentMethod,
            payment_amount: props.totalAmount,
          });
          props.onConfirm(
            props.totalAmount,
            payment.id,
            terminalStatus?.type === "nakka"
              ? terminalStatus.slip?.tip_amount || undefined
              : undefined,
          );
        } else if (terminalStatus?.status === "TERMINATED") {
          status.value = "terminated";
          statusMessage.value = `Payment failed: ${"Unknown error"}`;
          pollingResilience.stopPolling();
          instrumentation.track("checkout_terminal_payment_terminated", {
            checkout_id: props.checkoutId,
            payment_id: payment?.id,
            payment_method: props.paymentMethod,
          });
        } else {
          // Still processing
          statusMessage.value = "Processing payment...";
        }
      } catch (error: any) {
        // Strategy 5: Ignore abort errors as they're intentional
        if (pollingResilience.isAbortError(error)) return;

        console.error("Error polling transaction:", error);

        instrumentation.track("checkout_terminal_payment_poll_error", {
          checkout_id: props.checkoutId,
          payment_transaction_id: transactionId.value,
          payment_method: props.paymentMethod,
          error_message: error?.message,
          consecutive_failures: pollingResilience.consecutiveFailures.value + 1,
        });

        // Increment failure counter
        pollingResilience.incrementFailures();

        // Strategy 1: Exponential Backoff for Polling
        pollingResilience.updatePollingInterval(
          pollingResilience.consecutiveFailures.value,
        );

        // Strategy 6: Better Error Messaging
        statusMessage.value = pollingResilience.getStatusMessage(
          "Processing payment...",
        );

        // Restart polling with new interval
        pollingResilience.startPolling(pollTransactionStatus);
      }
    };

    // Strategy 1: Network Status Detection - Watch for network changes
    watch(
      () => pollingResilience.isOnline.value,
      (online) => {
        if (online) {
          // Resume polling if we have a transaction ID and we're in processing state
          if (transactionId.value && status.value === "processing") {
            statusMessage.value =
              "Connection restored. Checking payment status...";
            pollingResilience.resetFailures();
            pollingResilience.resetPollingInterval();
            pollingResilience.startPolling(pollTransactionStatus);
          }
        } else {
          // Network went offline
          if (status.value === "processing") {
            statusMessage.value =
              "No internet connection. Waiting for connection...";
          }
        }
      },
    );

    const abortTransaction = async () => {
      status.value = "aborting";
      statusMessage.value = "Aborting transaction...";
      const statusReq = await routingFetch(
        `/api/v2/payments/transactions/gateway-result/${transactionId.value}`,
      );
      const statusJson: any = await statusReq.json();
      // todo: clean this up and type it properly
      if (
        statusJson.status === "SUCCESS" ||
        statusJson.status?.status === "SUCCESS"
      ) {
        status.value = "processing";
        statusMessage.value =
          "Processing payment. Please follow instructions on terminal...";
        return alert(
          "Transaction already succeded. Please wait for a few more seconds for it to be processed",
        );
      }
      if (
        statusJson.status === "TERMINATED" ||
        statusJson.status?.status === "TERMINATED"
      ) {
        status.value = "terminated";
        statusMessage.value = "Transaction terminated";
        return;
      }

      try {
        const resp = await routingFetch(
          `/api/v2/payments/transactions/${transactionId.value}/abort`,
          {
            method: "PUT",
            body: JSON.stringify({
              checkout: null,
            }),
          },
        );
        if (resp.ok) {
          status.value = "aborted";
          statusMessage.value = "Transaction aborted";
          instrumentation.track("checkout_terminal_payment_aborted", {
            checkout_id: props.checkoutId,
            payment_method: props.paymentMethod,
          });
        } else {
          throw new Error("Failed to abort Terminal transaction");
        }
      } catch (error) {
        console.error("Error aborting transaction:", error);
        status.value = "errored";
        statusMessage.value = "Failed to abort transaction";
      } finally {
        pollingResilience.stopPolling();
        props.onClose();
      }
    };

    onMounted(() => {
      startTransaction();
    });

    // Clean up on component unmount (resilience composables handle cleanup automatically)
    onUnmounted(() => {
      pollingResilience.stopPolling();
    });

    return () => (
      <div>
        <LemmonModal
          title="TERMINAL PAYMENT"
          onClose={() => {
            props.onClose();
          }}
          disableCloseByClickingBackdrop={true}
          hideClose={true}
          renderButtons={() => (
            <div class="p-4 border-t ">
              <div class="flex justify-between gap-2">
                {["aborted", "errored", "terminated"].includes(status.value) ? (
                  <button
                    onClick={() => {
                      props.onClose();
                    }}
                    class="btn btn-neutral"
                    type="button"
                  >
                    Close
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      abortTransaction();
                    }}
                    class="btn btn-neutral"
                    type="button"
                    disabled={["initializing", "aborting"].includes(
                      status.value,
                    )}
                  >
                    Abort Transaction
                  </button>
                )}
                {props.paymentMethod === "card" ? (
                  <div class="flex-1 flex justify-end items-center text-warning text-xs">
                    To cancel, please use the cancel button on the terminal.
                  </div>
                ) : null}
              </div>
            </div>
          )}
        >
          <div class="p-4 flex flex-col items-center text-center">
            <div class="py-2 flex justify-center">
              {status.value === "initializing" ||
              status.value === "processing" ? (
                <div class="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
              ) : status.value === "success" ? (
                <div class="text-green-500">
                  <span class="material-symbols-outlined text-6xl">
                    check_circle
                  </span>
                </div>
              ) : (
                <div class="text-red-500">
                  <span class="material-symbols-outlined text-6xl">error</span>
                </div>
              )}
            </div>

            <div class="space-y-1 text-accent">
              <div class="flex px-4 flex-col items-center justify-center">
                <div class="flex-1 font-semibold flex items-center text-1xl">
                  Amount
                </div>
                <div class="flex-1 text-1xl text-right">
                  {auth.user.value?.restaurant.currency || "CHF"}{" "}
                  {props.totalAmount.toFixed(2)}
                </div>
              </div>

              <div class="text-xl font-medium">
                {statusMessage.value.replace(
                  "Please close this one first!",
                  "",
                )}
              </div>
            </div>
          </div>
        </LemmonModal>
      </div>
    );
  },
});
